import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  Badge,
  EmptyState,
  ErrorState,
  FilterChip,
  IconInfoCircle,
  ItemList,
  ItemListRow,
  PageHeader,
  Skeleton,
  StatusHint,
} from '@indipia/ui-core';
import { ShareLoadSkeleton, ShareStateScreen, classifyShareError, type ShareError } from '@indipia/shared-features';
import { fetchOrderList } from '../../api/orderListShareApi';
import {
  FILTERS,
  TEXT,
  filterCount,
  itemHint,
  itemTarget,
  itemTitle,
  linkText,
  parseOrderList,
  sortForDisplay,
  statusBadge,
  type ListFilter,
  type OrderListView,
} from './vendorOrderList.logic';

/**
 * 받은 발주 목록 (외부·무인증 공유 화면, share/SUPPLIER_ORDER_LIST).
 * 거래처가 알림톡 [받은 발주 전체 보기] 또는 회신 화면의 [☰ 받은 발주]로 연다. 자격증명은 공유 쿠키(목록 토큰)뿐이며
 * 화면은 토큰·shareId 를 다루지 않는다. 행을 고르면 상태별 단일 목적지(회신·배송정보·읽기 전용 회신)로 이동하고,
 * 이동한 화면은 쿼리 orderVendorId 로 목록 토큰의 건별 열람을 요청한다.
 */

type ScreenKind = 'SESSION' | 'LINK' | 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'NETWORK' | 'SERVER' | 'UNKNOWN';
const SCREEN_KINDS = new Set<string>(['SESSION', 'LINK', 'CLOSED', 'EXPIRED', 'CHANGED', 'NETWORK', 'SERVER', 'UNKNOWN']);
const RETRYABLE = new Set<ScreenKind>(['NETWORK', 'SERVER', 'UNKNOWN']);

function screenKindOf(e: ShareError | undefined): ScreenKind {
  return e && SCREEN_KINDS.has(e.kind) ? (e.kind as ScreenKind) : 'UNKNOWN';
}

function Shell({ children }: { children: ReactNode }) {
  // data-group="03" = 인디플래너 그룹 액센트
  return (
    <div data-group="03" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col gap-4 p-4" style={{ background: 'var(--shell-bg)' }}>
        {children}
      </div>
    </div>
  );
}

interface ListState {
  status: 'loading' | 'ready' | 'error';
  view?: OrderListView;
  error?: ScreenKind;
}

export function VendorOrderListPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ListFilter>('WAIT');
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<ListState>({ status: 'loading' });

  useEffect(() => {
    document.title = TEXT.documentTitle;
  }, []);

  // 칩을 바꿀 때마다 서버에서 다시 받는다(건수는 응답마다 전체 기준으로 내려온다). 이전 요청은 취소하고 취소는 상태에 반영하지 않는다.
  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading' }));
    fetchOrderList(filter, ctrl.signal).then(
      (raw) => {
        if (ctrl.signal.aborted) return;
        const parsed = parseOrderList(raw);
        setState(parsed.ok ? { status: 'ready', view: parsed.view } : { status: 'error', error: 'UNKNOWN' });
      },
      (e: unknown) => {
        if (ctrl.signal.aborted) return;
        setState((s) => ({ view: s.view, status: 'error', error: screenKindOf(classifyShareError(e)) }));
      },
    );
    return () => ctrl.abort();
  }, [filter, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const view = state.view;
  const items = useMemo(() => (view ? sortForDisplay(filter, view.items) : []), [view, filter]);

  // 처음 불러오기 전 상태
  if (!view) {
    if (state.status === 'loading') return <ShareLoadSkeleton />;
    const kind = state.error ?? 'UNKNOWN';
    return <ShareStateScreen kind={kind} onRetry={RETRYABLE.has(kind) ? reload : undefined} />;
  }
  // 링크 자체가 못 쓰게 된 오류는 목록을 두지 않고 전체 화면으로 알린다.
  if (state.status === 'error' && state.error && !RETRYABLE.has(state.error)) {
    return <ShareStateScreen kind={state.error} />;
  }

  const loading = state.status === 'loading';
  const failed = state.status === 'error';

  return (
    <Shell>
      <PageHeader title={TEXT.headerTitle(view.companyName)} subtitle={TEXT.headerSub(view.vendorName, view.windowDays) || undefined} />

      <div role="group" aria-label="보기" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <FilterChip
            key={f.code}
            size="lg"
            active={filter === f.code}
            aria-pressed={filter === f.code}
            count={filterCount(view.counts, f.code)}
            onClick={() => setFilter(f.code)}
          >
            {f.label}
          </FilterChip>
        ))}
      </div>

      <div aria-live="polite" aria-busy={loading}>
        {loading ? (
          <div className="flex flex-col gap-2">
            <Skeleton height="4rem" />
            <Skeleton height="4rem" />
            <Skeleton height="4rem" />
          </div>
        ) : failed ? (
          <ErrorState title={TEXT.listFail} onRetry={reload} />
        ) : items.length === 0 ? (
          <EmptyState icon={<IconInfoCircle />} title={TEXT.empty(view.windowDays)} />
        ) : (
          <ItemList>
            {items.map((it) => {
              const badge = statusBadge(it.statusCode);
              const hint = itemHint(it);
              return (
                <ItemListRow
                  key={it.orderVendorId}
                  title={itemTitle(it)}
                  hint={hint ? <StatusHint>{hint}</StatusHint> : undefined}
                  link={linkText(it.nextAction)}
                  trailing={badge ? <Badge status={badge.tone}>{badge.label}</Badge> : undefined}
                  onClick={() => navigate(itemTarget(it))}
                />
              );
            })}
          </ItemList>
        )}
      </div>
    </Shell>
  );
}
